import React from 'react';
import random from 'lodash/random';

import { TFunction, Trans } from '@lyearn/i18n';
import ColorOrEmojiPicker from '@/components/ColorOrEmojiPicker';
import { ErrorPosition, Fields, FieldType, useFormFieldValue } from '@/components/Form';
import { CreateMetricInput, CustomFrequencyEnum, KeyResultMetricEnum } from '@/types/schema';

import HabitCustomFrequency from './components/HabitCustomFrequency';
import HabitMetricGroup from './components/HabitMetricGroup';
import HabitReminders from './components/HabitReminders';
import { HabitEditorProps } from './types';

interface Params {
  t: TFunction;
  initialValues: HabitEditorProps['initialValues'];
  isEdit?: boolean;
}

export const getHabitEditorFormFields = (params: Params): Fields<CreateMetricInput> => {
  const { t, isEdit, initialValues } = params;

  return {
    name: {
      name: 'name',
      type: FieldType.Text,
      label: t('Common:Name'),
      placeholder: t(`Habits:Editor.NamePlaceholder-${random(0, 8)}`),
      size: 'large',
      props: { characterLimit: 48 },
    },
    iconSettings: {
      name: 'iconSettings',
      type: FieldType.Custom,
      Component: ({ value, onChange }) => {
        return <ColorOrEmojiPicker className="ms-8" value={value as TSFixMe} onChange={onChange} />;
      },
    },
    measurementUnit: {
      name: 'measurementUnit',
      type: FieldType.SlowCustom,
      label: t('Metrics:Editor.MeasureIn'),
      Component: ({ value, onChange, setFieldValue, error, touched }) => {
        const defaultTargetValue = useFormFieldValue('defaultTargetValue');
        const defaultTargetValueUnit = useFormFieldValue('defaultTargetValueUnit');

        return (
          <HabitMetricGroup
            error={touched ? error : undefined}
            isEdit={isEdit}
            metric={value || KeyResultMetricEnum.Number}
            setFieldValue={setFieldValue}
            targetValue={defaultTargetValue ?? 0}
            targetValueUnit={defaultTargetValueUnit ?? ''}
            onChangeMetric={onChange}
          />
        );
      },
    },
    repeatSchedule: {
      name: 'repeatSchedule',
      type: FieldType.Custom,
      errorPosition: ErrorPosition.Custom,
      label: t('Habits:Editor.Repeat'),
      Component: ({ value, onChange, error }) => (
        <HabitCustomFrequency
          error={error}
          initialValue={initialValues.repeatSchedule}
          isEdit={isEdit}
          value={value as TSFixMe}
          onChange={onChange}
        />
      ),
    },
    reminders: {
      name: 'reminders',
      type: FieldType.Custom,
      label: t('Habits:Editor.RemindMeAt'),
      errorPosition: ErrorPosition.Custom,
      Component: ({ value, onChange, error }) => (
        <HabitReminders error={error} value={value as TSFixMe} onChange={onChange} />
      ),
    },
  };
};

export const validateHabitEditorForm = (values: CreateMetricInput) => {
  const errors: TSFixMe = {};

  if (!values.name.trim()) {
    errors.name = <Trans i18nKey="Habits:Editor.NameError" />;
  }

  if (values.measurementUnit === KeyResultMetricEnum.Number && !values.defaultTargetValue) {
    errors.measurementUnit = <Trans i18nKey="Habits:Editor.GoalError" />;
  }

  if (values.measurementUnit === KeyResultMetricEnum.Time && !values.defaultTargetValue) {
    errors.measurementUnit = <Trans i18nKey="Habits:Editor.GoalError" />;
  }

  const { frequency, daysOfWeek, weeksOfMonth } = values.repeatSchedule?.custom || {};

  if (
    (frequency === CustomFrequencyEnum.Week || frequency === CustomFrequencyEnum.Month) &&
    !daysOfWeek?.length
  ) {
    errors.repeatSchedule = {
      ...errors.repeatSchedule,
      daysOfWeek: <Trans i18nKey="Habits:Editor.DaysOfWeekError" />,
    };
  }

  if (frequency === CustomFrequencyEnum.Month && !weeksOfMonth?.length) {
    errors.repeatSchedule = {
      ...errors.repeatSchedule,
      weeksOfMonth: <Trans i18nKey="Habits:Editor.WeeksOfMonthError" />,
    };
  }

  const valueSet = new Set();
  const remindersDuplicateValues: string[] = [];

  values.reminders?.forEach((reminder) =>
    valueSet.has(reminder) ? remindersDuplicateValues.push(reminder) : valueSet.add(reminder),
  );

  if (remindersDuplicateValues.length > 0) {
    errors.reminders = remindersDuplicateValues;
  }

  return errors;
};
